import React, { useCallback, useEffect, useState } from "react";
import { ShieldAlert, Plus, X, Video } from "lucide-react";

import Button from "../../../components/forms/Button";
import { monitoringService } from "../../../api/monitoringService";
import { CAPTURE_MODE_LABEL, employeeName, formatDateTime } from "../utils/format";

const inputClass =
  "w-full px-3 py-2.5 bg-white dark:bg-dark-100 border border-gray-200 dark:border-dark-200 rounded-lg text-sm text-gray-900 dark:text-light-100 focus:ring-2 focus:ring-primary-accent focus:border-transparent outline-none transition-all";

/** Default the expiry to a week out, which is the common case. */
const defaultExpiry = () => {
  const d = new Date(Date.now() + 7 * 86400000);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Grant and revoke per-employee capture overrides — "video-on-demand".
 *
 * The global default is cheap screenshots; switching one named person to full
 * video is a materially bigger intrusion, so the backend REQUIRES an expiry and a
 * reason and caps the duration at 30 days. This form mirrors those rules rather
 * than letting the server reject the submission.
 */
const OverridesPanel = ({ employees, onChange, showToast }) => {
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    employeeId: "",
    captureMode: "video",
    expiresAt: defaultExpiry(),
    reason: "",
    fps: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await monitoringService.getOverrides({ limit: 50 });
      setOverrides(data.records || []);
    } catch (err) {
      showToast(
        err?.response?.data?.message || "Could not load overrides",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employeeId || !form.reason.trim()) return;
    setSaving(true);
    try {
      await monitoringService.createOverride({
        employeeId: form.employeeId,
        captureMode: form.captureMode,
        // datetime-local has no timezone; send an absolute instant.
        expiresAt: new Date(form.expiresAt).toISOString(),
        reason: form.reason.trim(),
        fps: form.fps ? Number(form.fps) : undefined,
      });
      showToast("Override granted", "success");
      setShowForm(false);
      setForm((f) => ({ ...f, employeeId: "", reason: "", fps: "" }));
      load();
      onChange?.();
    } catch (err) {
      showToast(
        err?.response?.data?.message ||
          err?.response?.data?.details?.[0] ||
          "Could not grant the override",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (id) => {
    try {
      await monitoringService.revokeOverride(id);
      showToast("Override revoked", "success");
      load();
      onChange?.();
    } catch (err) {
      showToast(
        err?.response?.data?.message || "Could not revoke the override",
        "error",
      );
    }
  };

  const activeOnes = overrides.filter((o) => o.isActive);
  const pastOnes = overrides.filter((o) => !o.isActive);

  return (
    <div className="bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-dark-100 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-primary-accent" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-light-100">
            Video on demand
          </h2>
        </div>
        <Button
          size="sm"
          onClick={() => setShowForm((s) => !s)}
          className="rounded-lg"
        >
          <Plus className="w-4 h-4" />
          Grant
        </Button>
      </div>

      <div className="px-4 py-3 text-xs text-gray-500 dark:text-light-500 border-b border-gray-200 dark:border-dark-100">
        Everyone is on the global capture mode by default. Use this to switch one
        employee to full video for a limited period — it always expires, a reason is
        required, and the maximum is 30 days.
      </div>

      {showForm && (
        <form
          onSubmit={submit}
          className="p-4 border-b border-gray-200 dark:border-dark-100 space-y-3 bg-gray-50 dark:bg-dark-300/30"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 dark:text-light-500 mb-1.5">
                Employee *
              </label>
              <select
                required
                value={form.employeeId}
                onChange={(e) =>
                  setForm({ ...form, employeeId: e.target.value })
                }
                className={inputClass}
              >
                <option value="">Select an employee…</option>
                {employees.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.fullNameEn}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs text-gray-500 dark:text-light-500 mb-1.5">
                Capture mode
              </label>
              <select
                value={form.captureMode}
                onChange={(e) =>
                  setForm({ ...form, captureMode: e.target.value })
                }
                className={inputClass}
              >
                <option value="video">Video only</option>
                <option value="both">Video + screenshots</option>
                <option value="screenshots">Screenshots only</option>
                <option value="off">Off (no capture)</option>
              </select>
            </div>

            <div>
              <label className="block text-xs text-gray-500 dark:text-light-500 mb-1.5">
                Expires *
              </label>
              <input
                required
                type="datetime-local"
                value={form.expiresAt}
                onChange={(e) =>
                  setForm({ ...form, expiresAt: e.target.value })
                }
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-xs text-gray-500 dark:text-light-500 mb-1.5">
                fps override (optional)
              </label>
              <input
                type="number"
                min="1"
                max="60"
                placeholder="use the global setting"
                value={form.fps}
                onChange={(e) => setForm({ ...form, fps: e.target.value })}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 dark:text-light-500 mb-1.5">
              Reason * — recorded permanently for audit
            </label>
            <input
              required
              minLength={3}
              maxLength={500}
              placeholder="e.g. Investigating a client complaint about missed deadlines"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              className={inputClass}
            />
          </div>

          <div className="flex gap-2">
            <Button
              size="sm"
              type="submit"
              disabled={saving || !form.employeeId || !form.reason.trim()}
              className="rounded-lg"
            >
              {saving ? "Granting…" : "Grant override"}
            </Button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-3 py-1.5 text-sm text-gray-500 dark:text-light-500 hover:text-gray-700 dark:hover:text-light-300"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-accent" />
        </div>
      ) : overrides.length === 0 ? (
        <div className="py-10 text-center text-sm text-gray-500 dark:text-light-500">
          No overrides. Everyone is on the global capture mode.
        </div>
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-dark-100">
          {[...activeOnes, ...pastOnes].map((o) => (
            <div
              key={o._id}
              className="px-4 py-3 flex items-start gap-3 flex-wrap sm:flex-nowrap"
            >
              <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  o.isActive
                    ? "bg-primary-accent/15 text-primary-accent"
                    : "bg-gray-100 dark:bg-dark-200 text-gray-400"
                }`}
              >
                <Video className="w-4 h-4" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-gray-900 dark:text-light-100">
                    {employeeName(o.employeeId)}
                  </p>
                  <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-dark-200 text-gray-600 dark:text-light-400">
                    {CAPTURE_MODE_LABEL[o.captureMode] ?? o.captureMode}
                  </span>
                  {o.isActive ? (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                      active
                    </span>
                  ) : (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-dark-200 text-gray-500 dark:text-light-500">
                      {o.revokedAt ? "revoked" : "expired"}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-light-500 mt-0.5 break-words">
                  {o.reason}
                </p>
                <p className="text-[11px] text-gray-400 dark:text-light-500 mt-0.5">
                  {o.isActive ? "expires" : "expired"}{" "}
                  {formatDateTime(o.expiresAt)}
                  {o.createdBy?.fullNameEn
                    ? ` · granted by ${o.createdBy.fullNameEn}`
                    : ""}
                </p>
              </div>

              {o.isActive && (
                <button
                  onClick={() => revoke(o._id)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex-shrink-0"
                  title="Revoke now"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OverridesPanel;
